import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BookOpen, Download, ArrowLeft, Wrench, ClipboardList, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Wrench, ClipboardList, Crown, BookOpen };

export default function Manuales() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/manuales").then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!slug) { setCurrent(null); return; }
    setLoading(true);
    api.get(`/manuales/${slug}`)
      .then((r) => setCurrent(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "No se pudo cargar el manual"))
      .finally(() => setLoading(false));
  }, [slug]);

  const downloadPdf = async () => {
    if (!current) return;
    setDownloading(true);
    try {
      const res = await api.get(`/manuales/${current.slug}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `manual_${current.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error generando el PDF");
    } finally { setDownloading(false); }
  };

  // Vista lista
  if (!slug) {
    return (
      <div className="space-y-6" data-testid="manuales-page">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-amber-400" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Manuales</h1>
            <p className="text-sm text-zinc-500">Guías de uso de la aplicación adaptadas a tu rol.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando manuales…
          </div>
        ) : list.length === 0 ? (
          <div className="border border-zinc-800 rounded-lg p-12 text-center text-zinc-500">
            No hay manuales disponibles para tu rol.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((m) => {
              const Icon = ICONS[m.icon] || BookOpen;
              return (
                <button key={m.slug} onClick={() => navigate(`/manuales/${m.slug}`)}
                        data-testid={`manual-card-${m.slug}`}
                        className="text-left bg-zinc-900/40 border border-zinc-800 hover:border-amber-500 hover:bg-zinc-900/70 rounded-lg p-5 transition group">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-700/40 flex items-center justify-center group-hover:bg-amber-500/20">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-zinc-100">{m.titulo}</div>
                      {m.destinatarios && (
                        <div className="text-[11px] text-zinc-500 mt-0.5">{m.destinatarios}</div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-zinc-400">{m.descripcion}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Vista detalle
  return (
    <div className="space-y-4" data-testid="manual-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/manuales")} className="text-zinc-400 hover:text-zinc-100"
                  data-testid="manual-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          {current && (
            <div>
              <h1 className="text-2xl font-bold">{current.titulo}</h1>
              {current.destinatarios && (
                <p className="text-xs text-zinc-500">{current.destinatarios}</p>
              )}
            </div>
          )}
        </div>
        {current && (
          <Button onClick={downloadPdf} disabled={downloading}
                  data-testid="manual-download-pdf"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando…</>
                         : <><Download className="w-4 h-4 mr-2" /> Descargar PDF</>}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : current ? (
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-6 md:p-10 max-w-4xl mx-auto">
          <article className="manual-content"
                   dangerouslySetInnerHTML={{ __html: current.html }} />
        </div>
      ) : (
        <div className="text-center text-zinc-500 py-12">Manual no encontrado.</div>
      )}

      {/* Estilos específicos para el HTML markdown renderizado */}
      <style>{`
        .manual-content h1 { color: #fbbf24; font-size: 1.875rem; font-weight: 700; border-bottom: 2px solid #b45309; padding-bottom: 0.5rem; margin: 0 0 1rem; }
        .manual-content h2 { color: #fcd34d; font-size: 1.375rem; font-weight: 600; margin: 2rem 0 0.75rem; border-bottom: 1px solid #3f3f46; padding-bottom: 0.35rem; }
        .manual-content h3 { color: #fde68a; font-size: 1.125rem; font-weight: 600; margin: 1.5rem 0 0.5rem; }
        .manual-content p { color: #d4d4d8; line-height: 1.65; margin: 0.5rem 0; }
        .manual-content ul, .manual-content ol { color: #d4d4d8; padding-left: 1.5rem; margin: 0.5rem 0; }
        .manual-content li { margin: 0.35rem 0; line-height: 1.6; }
        .manual-content code { background: #18181b; color: #fde68a; padding: 0.1rem 0.4rem; border-radius: 0.25rem; font-size: 0.875em; }
        .manual-content pre { background: #18181b; color: #d4d4d8; padding: 0.85rem; border-radius: 0.4rem; overflow-x: auto; border: 1px solid #27272a; }
        .manual-content blockquote { border-left: 3px solid #f59e0b; background: rgba(245, 158, 11, 0.05); padding: 0.6rem 1rem; color: #a1a1aa; margin: 1rem 0; border-radius: 0.25rem; }
        .manual-content table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
        .manual-content th, .manual-content td { border: 1px solid #3f3f46; padding: 0.5rem 0.75rem; text-align: left; }
        .manual-content th { background: rgba(245, 158, 11, 0.1); color: #fcd34d; font-weight: 600; }
        .manual-content td { color: #d4d4d8; }
        .manual-content hr { border: 0; border-top: 1px solid #3f3f46; margin: 1.5rem 0; }
        .manual-content a { color: #fbbf24; text-decoration: underline; }
        .manual-content strong { color: #fafafa; }
      `}</style>
    </div>
  );
}
